import { colors, getCssVar } from 'quasar'

import { getI18n } from '@/plugins/i18n'

import { stringToColor, textColor } from '@/lib/utils'

import { usePortalStore } from '@/stores/portal.store'
import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePlanningStore } from '@/stores/planning.store'
import { useScheduleStore, eventTypeEnum } from '@/stores/schedule.store'

export const useChips = () => {
  const portalStore = usePortalStore()
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()
  const planningStore = usePlanningStore()
  const scheduleStore = useScheduleStore()

  const i18n = getI18n()
  const localizedTags = portalStore.getLocalizedTags()
  const budgetDocumentTags = accountingStore.getLocalizedBudgetTags()

  const getServiceAvailabilityChip = (e) => {
    const availabilityType = portalStore.getLocalizedServiceAvailabilityTypes().find(t => t.id === e.availability_type)
    const color = availabilityType?.id === portalStore.serviceAvailabilityTypeEnum.USUALLY_AVAILABLE ? 'green'
      : availabilityType?.id === portalStore.serviceAvailabilityTypeEnum.USUALLY_UNAVAILABLE ? 'warning'
        : 'orange'

    return {
      label: availabilityType ? availabilityType.name : e.availability,
      color: color,
      style: { 'background-color': color }
    }
  }

  const archivedChip = {
    id: -1,
    name: i18n.t('common.archived'),
    label: i18n.t('common.archived'),
    color: 'grey',
    textColor: textColor('grey'),
    size: 'sm',
  }

  const mineChip = {
    id: -8,
    name: i18n.t('common.expenseClaimMine'),
    label: i18n.t('common.expenseClaimMine'),
    icon: 'mdi-account',
    color: 'primary',
    textColor: 'white',
    size: 'sm',
  }

  const multipleProjectsChip = {
    id: -9,
    name: i18n.t('common.multipleProjects'),
    label: i18n.t('common.multipleProjects'),
    color: 'grey-6',
    textColor: 'white',
    size: 'sm',
  }

  const rentedChip = {
    id: -2,
    name: i18n.t('common.rented'),
    label: i18n.t('common.rented'),
    color: 'grey',
    textColor: textColor('grey'),
    size: 'sm',
  }

  const rentalChip = {
    id: -3,
    name: i18n.t('common.rental'),
    label: i18n.t('common.rental'),
    color: 'grey',
    textColor: textColor('grey'),
    size: 'sm',
  }

  const needQrScanChip = {
    id: -5,
    name: i18n.t('common.needQrScan'),
    label: i18n.t('common.needQrScan'),
    icon: 'mdi-qrcode-scan',
    color: 'violet',
    textColor: textColor('violet'),
    size: 'sm',
  }

  const isVehicleChip = {
    id: -6,
    name: i18n.t('common.vehicleInfo'),
    label: i18n.t('common.vehicleInfo'),
    icon: 'mdi-car',
    color: 'teal',
    textColor: textColor('teal'),
    size: 'sm',
  }

  const isSerializedChip = {
    id: -7,
    name: i18n.t('common.isSerialized'),
    label: i18n.t('common.isSerialized'),
    icon: 'mdi-barcode',
    color: 'indigo',
    textColor: textColor('indigo'),
    size: 'sm',
  }

  const clientChip = {
    id: -4,
    name: i18n.t('common.client'),
    label: i18n.t('common.client'),
    color: 'green',
    textColor: textColor('green'),
    size: 'sm',
  }

  const getCompanyChips = (company) => {
    const chips = []
    if (company.is_rental) {
      chips.push(rentalChip)
    }
    if (company.is_client) {
      chips.push(clientChip)
    }
    return chips
  }

  const getListingChips = (listing) => {
    const chips = []
    if (listing.company) {
      const company = planningStore.companies.find(c => c.id === listing.company)
      if (company) {
        chips.push({
          id: company.id,
          label: company?.name,
          color: stringToColor(company?.name),
          textColor: textColor(stringToColor(company?.name)),
          size: 'sm',
        })
      }
    }
    return chips
  }

  const getBudgetDocumentEstimateChip = (isEstimate) => {
    const label = isEstimate ? i18n.t('common.estimate') : i18n.t('common.budget')
    const color = isEstimate ? 'orange' : 'positive'
    return {
      id: isEstimate ? 1 : 0,
      name: label,
      color: color,
    }
  }

  const toTagChips = (tagIds, tags) => tagIds.map(id => {
    const tag = tags.find(t => t.id === id)
    if (!tag?.name) return null
    const color = stringToColor(tag.translations?.[window.config.DEFAULT_LANGUAGE]?.name ?? tag.name)
    return { id, name: tag.name, color, textColor: textColor(color) }
  }).filter(Boolean)

  const getBudgetDocumentTagChips = (tagIds) => (tagIds?.length ? toTagChips(tagIds, budgetDocumentTags) : [])

  const searchMatchColor = getCssVar('primary')
  const searchMatchTextColor = textColor(searchMatchColor)
  const getSearchMatchChips = (labels) =>
    labels.map(label => ({ id: `search-${label}`, name: label, color: searchMatchColor, textColor: searchMatchTextColor }))

  const getProjectTagChips = (tagIds) => (tagIds?.length ? toTagChips(tagIds, planningStore.getLocalizedProjectTags()) : [])

  const getBudgetDocumentRowTypeChip = (type) => {
    const label = type === webSiteStore.documentRowTypeEnum.COMMENT ? i18n.t('common.documentRowTypes.comment')
      : type === webSiteStore.documentRowTypeEnum.EXTRA ? i18n.t('common.documentRowTypes.extra')
        : i18n.t('common.documentRowTypes.listing_item')
    const color = type === webSiteStore.documentRowTypeEnum.COMMENT ? 'info'
      : type === webSiteStore.documentRowTypeEnum.EXTRA ? 'positive'
        : 'grey'
    return {
      id: type,
      name: label,
      color: color,
    }
  }

  const getTagChips = (tags) => {
    if (!tags || tags.length === 0) {
      return []
    }
    return tags.map(tag => {
      const tagInstance = localizedTags.find(t => t.id === tag)
      if (!tagInstance?.name) return null
      const color = stringToColor(tagInstance.translations?.[window.config.DEFAULT_LANGUAGE]?.name ?? tagInstance.name)

      return {
        id: tag,
        label: tagInstance.name,
        color: color,
        textColor: textColor(color),
        style: { 'background-color': color, 'color': textColor(color)}
      }
    }).filter(Boolean)
  }

  const getGroupChips = (groupIds) => {
    if (!groupIds?.length) return []
    return groupIds.map(id => {
      const group = planningStore.getGroupById(id)
      if (!group?.name) return null
      const color = stringToColor(group.name)
      return { id, name: group.name, color, textColor: textColor(color) }
    }).filter(Boolean)
  }

  const getConditionChips = (conditionValue) => {
    if (conditionValue == null) return []
    const color = planningStore.getConditionColor(conditionValue)
    return [{ name: planningStore.getConditionLabel(conditionValue), color, textColor: textColor(color) }]
  }

  const getServiceStructureTypeChip = (structureType) => {
    if (structureType == null) return []
    const found = portalStore.getLocalizedServiceStructureTypes().find(t => t.id === structureType)
    return found ? [found] : []
  }

  const getVirtualLocationChip = () => {
    const { color } = portalStore.getLocalizedServiceStructureTypes()
      .find(t => t.id === portalStore.serviceStructureTypeEnum.GROUP_VIRTUAL)
    return [{ id: 'virtual-location', name: i18n.t('common.virtual'), color, textColor: textColor(color), icon: 'mdi-map-marker-off-outline' }]
  }

  const getServiceTagChips = (tags) => {
    if (!tags || tags.length === 0) return []
    const planningTagsList = planningStore.getLocalizedServiceTags()
    return tags.map(tag => {
      const tagInstance = planningTagsList.find(t => t.id === tag)
      if (!tagInstance?.name) return null
      const color = stringToColor(tagInstance.translations?.[window.config.DEFAULT_LANGUAGE]?.name ?? tagInstance.name)
      return { id: tag, name: tagInstance.name, color: color, textColor: textColor(color) }
    }).filter(Boolean)
  }

  const getWarehouseMovementTypeChips = (movementType) => {
    if (movementType == null) return []
    const found = planningStore.getLocalizedWarehouseMovementTypes().find(t => t.id === movementType)
    if (!found) return []
    const color = stringToColor(found.label)
    return [{ id: found.id, name: found.name, color, textColor: textColor(color) }]
  }

  const getStaffMovementTypeChips = (movementType) => {
    if (movementType == null) return []
    const found = planningStore.getLocalizedStaffMovementTypes().find(t => t.id === movementType)
    if (!found) return []
    const color = stringToColor(found.label)
    return [{ id: found.id, name: found.name, color, textColor: textColor(color) }]
  }

  const getWarehouseMovementStatusChips = (status) => {
    if (status == null) return []
    const found = planningStore.getLocalizedWarehouseMovementStatuses().find(t => t.id === status)
    if (!found) return []
    const color = stringToColor(found.label)
    return [{ id: found.id, name: found.name, color, textColor: textColor(color) }]
  }

  const getProjectChips = (projects) => (projects || []).map(project => {
    const color = project.color || colors.getPaletteColor('grey-3')
    return { id: `project-${project.id}`, name: project.name, color, textColor: textColor(color) }
  })

  const getProjectsSummaryChips = (projects) => (projects?.length > 1 ? [multipleProjectsChip] : getProjectChips(projects))

  const paletteChip = (id, name, palette, icon) => {
    const color = colors.getPaletteColor(palette)
    return { id, name, color, textColor: textColor(color), icon }
  }

  const getExpenseClaimStatusChips = (claim) => {
    const found = accountingStore.getLocalizedExpenseClaimStatuses().find(s => s.id === claim.status)
    return found ? [paletteChip(found.id, found.name, found.color)] : []
  }

  const getInvoiceStatusChips = (status) => {
    const found = accountingStore.getLocalizedInvoiceStatuses().find(s => s.id === status)
    if (!found) return []
    return [{ id: found.id, name: found.name, color: found.color, textColor: textColor(found.color) }]
  }

  const eventTypeColors = {
    [eventTypeEnum.PROJECT_ROOT]: 'blue-grey',
    [eventTypeEnum.ALLOCATION]: 'primary',
    [eventTypeEnum.ABSENCE]: 'purple',
  }

  const getEventTypeChips = (type) => {
    if (type == null) return []
    const found = scheduleStore.getLocalizedEventTypes().find(t => t.id === type)
    if (!found) return []
    const color = eventTypeColors[type] ?? 'grey'
    return [{ id: found.id, name: found.name, color, textColor: textColor(color) }]
  }

  const getVacationStatusChips = (confirmed) => [confirmed
    ? { id: 1, name: i18n.t('common.approved'), color: 'positive', textColor: 'white' }
    : { id: 0, name: i18n.t('common.pendingApproval'), color: 'warning', textColor: 'white' }]

  const getStaffChips = (name) => (name ? [{ id: 'staff', name, icon: 'mdi-account', color: 'grey-6', textColor: 'white', size: 'sm' }] : [])

  const getServiceTypeChips = (serviceType) => {
    if (serviceType == null) return []
    const found = portalStore.getLocalizedServiceTypes().find(t => t.id === serviceType)
    if (!found) return []
    return [{ id: found.id, name: found.name, color: found.color, textColor: found.textColor }]
  }

  return {
    getStaffChips,
    getConditionChips,
    getServiceAvailabilityChip,
    getCompanyChips,
    getListingChips,
    getGroupChips,
    getTagChips,
    getServiceTagChips,
    getServiceStructureTypeChip,
    getVirtualLocationChip,
    getProjectTagChips,
    getSearchMatchChips,
    getBudgetDocumentEstimateChip,
    getBudgetDocumentRowTypeChip,
    getBudgetDocumentTagChips,
    getWarehouseMovementTypeChips,
    getStaffMovementTypeChips,
    getWarehouseMovementStatusChips,
    getInvoiceStatusChips,
    getExpenseClaimStatusChips,
    getProjectChips,
    getProjectsSummaryChips,
    getEventTypeChips,
    getVacationStatusChips,
    getServiceTypeChips,
    archivedChip,
    mineChip,
    rentedChip,
    rentalChip,
    clientChip,
    needQrScanChip,
    isVehicleChip,
    isSerializedChip,
  }
}
